import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { classNames } from '@utils/class-names';
import { ReactComponent as DotsIcon } from "@assets/icons/dots.svg";
import { ReactComponent as ChevronIcon } from "@assets/icons/chevron.svg";
import AppButton from "@common/components/app-button";
import { ReactComponent as EyeSlashIcon } from "@assets/icons/eye-slash.svg";
import { ReactComponent as CloudeIcon } from "@assets/icons/cloude.svg";
import { useArticleForm } from "../hooks/use-article-form.hook";
import { ArticleFormStep } from "../contexts/article-form-context";
import { getStepSubtitle } from "../helpers/get-step-subtitle";
import { formArticleStep } from "../constants/form-article-step";
import { Tooltip } from "@common/components/tooltip";
import Popover, { PopoverHandle } from "@common/components/popover";
import ArticleActions from "./article-actions";
import { ExclamationCircleIcon } from "@heroicons/react/16/solid";
import { ArticleDetails } from "../../core/models/interfaces/article-details.interface";
import { useSpecialistApi } from "../../core/hooks/use-specialist-api";

const ArticleEditMenu: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isNewArticle = location.pathname.includes('/new');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const popoverRef = useRef<PopoverHandle>(null);
    const { getSpecialist } = useSpecialistApi();

    const {
        currentStep,
        setCurrentStep,
        stepsMetadata,
        formData,
        hasStepErrors,
        getStepErrors,
        clearStepErrors,
        markStepAsVisited,
        resetVisitedSteps,
        hideArticle,
        submitArticle,
        articleId,
        appId,
        areVersionIdentical,
        resetVersionsToPublished,
        isUniversal,
        isPostoperativeUniversal
    } = useArticleForm();


    const handleStepClick = (step: ArticleFormStep) => {
        setCurrentStep(step);
        clearStepErrors(step);
        markStepAsVisited(step);
    };

    const handlePublishClick = async (): Promise<void> => {
        resetVisitedSteps();
        await submitArticle();
    }

    const isArticleVisible: boolean = formData?.isVisible ?? false;
    const visibilityTooltip = isArticleVisible
        ? "Artykuł jest opublikowany i widoczny dla użytkowników."
        : "Artykuł jest ukryty i dostępny tylko w panelu edycji.";

    const getArticleData = async (): Promise<ArticleDetails> => {
        let author = undefined;

        if (formData?.authorId && formData?.authorName) {
            try {
                const specialist = await getSpecialist(formData.authorId);
                author = {
                    id: specialist.id,
                    fullName: specialist.fullName,
                    phoneNumber: specialist.phoneNumber || '',
                    specialization: specialist.specialization || ''
                };
            } catch (error) {
                author = {
                    id: formData.authorId,
                    fullName: formData.authorName,
                    phoneNumber: '',
                    specialization: ''
                };
            }
        }

        return {
            ...formData,
            id: articleId || '',
            appId: appId || '',
            author: author
        } as unknown as ArticleDetails;
    };

    return (
        <div
            className="flex flex-col justify-between pb-6 gap-4 h-full w-fit bg-surface-level-2 border-r border-outline-mild z-50">
            <div className="flex flex-col gap-4">
                <div className="pl-8 pr-6 border-b border-outline-mild h-[72px] flex items-center justify-between">
                    <h1 className="font-semibold text-lg">
                        {isNewArticle ? 'Tworzenie artykułu' : 'Edycja artykułu'}
                    </h1>
                    <Popover
                        ref={popoverRef}
                        onOpen={() => setIsPopoverOpen(true)}
                        onClose={() => setIsPopoverOpen(false)}
                        content={
                            <ArticleActions
                                canDelete={!isUniversal}
                                articleId={articleId}
                                getArticleData={getArticleData}
                                onClosePopover={popoverRef.current?.close} />
                        }
                        offset={0}>
                        <div className={classNames(
                            "h-10 w-10 flex justify-center items-center cursor-pointer rounded-full",
                            isPopoverOpen ? "bg-on-surface/5" : "hover:bg-on-surface/5"
                        )}>
                            <DotsIcon />
                        </div>
                    </Popover>
                </div>

                <div className="px-6 space-y-2">
                    {formArticleStep.map((step) => {
                        const Icon = step.icon;
                        const isCurrent = currentStep === step.key;
                        const hasErrors = hasStepErrors(step.key) && !isCurrent && !stepsMetadata[step.key].hasBeenVisited; // Nie pokazuj błędów dla odwiedzonych kroków

                        return (
                            <AppButton
                                key={step.key}
                                onClick={() => handleStepClick(step.key)}
                                className={classNames(
                                    'relative flex p-2 px-2 w-[300px] text-on-surface rounded-lg transition-colors duration-200',
                                    'border-0',
                                    isCurrent && 'bg-primary/10 border-l-4 !border-primary'
                                )}
                                classNameContentWrapper="w-full !block"
                            >
                                <div className="flex items-center h-full">
                                    <div className="h-full relative">
                                        <Icon className="mr-3 min-w-fit" />
                                        {hasErrors && <ExclamationCircleIcon
                                            className="z-1 absolute -top-[10px] -left-[10px] w-5 h-5 text-error" />}
                                    </div>
                                    <div className="mr-auto text-left">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">{step.label}</span>
                                        </div>
                                        <div className={classNames(
                                            "font-normal text-sm",
                                            hasErrors ? "text-error" : "text-on-surface/60"
                                        )}>
                                            {getStepSubtitle(step.key, formData!, stepsMetadata, hasErrors ? getStepErrors(step.key) : undefined)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <ChevronIcon className="text-on-surface/40" />
                                    </div>
                                </div>
                            </AppButton>
                        );
                    })}
                </div>

                <div className="border-t border-outline-mild pt-6 px-8 flex flex-col gap-4">
                    <div className="">
                        <div className="flex items-center text-on-surface/80 text-sm font-normal">
                            <span>
                                Artykuł jest
                                <span className={classNames(
                                    "font-semibold ml-1",
                                    isArticleVisible ? "text-success" : "text-error",
                                )}>
                                    {isArticleVisible ? 'widoczny' : 'ukryty'}
                                </span>
                            </span>
                            <Tooltip content={visibilityTooltip} className="ml-2 mt-[2px]" position="right" />
                            {isArticleVisible && !isUniversal && !isPostoperativeUniversal && (
                                <div className="flex-1 flex justify-end">
                                    <AppButton
                                        onClick={hideArticle}
                                        className="ml-auto !p-2 !px-4 !w-fit hover:bg-on-surface/5 border-0">
                                        <div className="flex gap-2 items-center text-on-surface font-semibold">
                                            Ukryj
                                            <EyeSlashIcon />
                                        </div>
                                    </AppButton>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <AppButton
                            onClick={() => navigate(-1)}
                            className="border-2 !border-on-surface text-base font-semibold hover:bg-on-surface/5"
                            label={isNewArticle ? "Zamknij edytor" : "Zakończ edycję"}
                        />
                        <AppButton
                            onClick={handlePublishClick}
                            className="bg-primary text-on-primary font-semibold text-base hover:!bg-primary/80"
                            label={isArticleVisible ? 'Opublikuj zmiany' : 'Opublikuj artykuł'}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col justify-center gap-3">
                {!isNewArticle && !areVersionIdentical && (
                    <AppButton
                        onClick={resetVersionsToPublished}
                        loaderColor="black"
                        className="border-0 bg-input-background/5 text-base font-semibold hover:bg-input-background/10 !py-0 px-1 max-w-fit mx-auto"
                        label="Przywróć poprzednią wersję"
                    />
                )}
                <div className="flex items-center justify-center text-sm px-8">
                    <span className={classNames("font-normal")}>
                        Zmiany są automatycznie zapisywane
                    </span>
                    <CloudeIcon className={classNames("ml-2")} />
                </div>
            </div>
        </div>
    );
};

export default ArticleEditMenu;
